import type { ThemeConfig } from 'antd';
import { theme as antdThemeAlgorithm } from 'antd';
import {
  borderRadius,
  fontFamily,
  primaryColor,
  primaryHover,
} from '../utils/variables';

const primaryTint = `${primaryColor}1A`;

const sharedComponents: ThemeConfig['components'] = {
  Button: {
    fontWeight: 600,
    controlHeight: 40,
    controlHeightLG: 44,
    primaryShadow: 'none',
    defaultShadow: 'none',
    dangerShadow: 'none',
  },
  Input: {
    controlHeight: 40,
    controlHeightLG: 44,
    colorBgContainer: 'var(--card)',
    activeBorderColor: primaryColor,
    hoverBorderColor: primaryColor,
  },
  InputNumber: {
    controlHeight: 40,
    colorBgContainer: 'var(--card)',
    activeBorderColor: primaryColor,
    hoverBorderColor: primaryColor,
    colorText: 'var(--foreground)',
    colorTextDisabled: 'var(--foreground)',
    handleBorderColor: 'var(--border)',
  },
  Select: {
    controlHeight: 40,
    selectorBg: 'var(--card)',
    optionSelectedBg: primaryTint,
    optionActiveBg: 'var(--menu-item-hover)',
    optionSelectedColor: primaryColor,
  },
  DatePicker: {
    controlHeight: 40,
    colorBgContainer: 'var(--card)',
    activeBorderColor: primaryColor,
    hoverBorderColor: primaryColor,
    cellHoverBg: 'var(--menu-item-hover)',
    cellActiveWithRangeBg: primaryTint,
  },
  Table: {
    headerBg: 'var(--table-header-bg)',
    rowHoverBg: 'var(--table-row-hover)',
    borderColor: 'var(--table-row-border)',
    headerColor: 'var(--muted-foreground)',
  },
  Modal: {
    borderRadiusLG: 12,
    contentBg: 'var(--card)',
    headerBg: 'var(--card)',
    titleColor: 'var(--foreground)',
    footerBg: 'var(--card)',
  },
  Drawer: {
    colorBgElevated: 'var(--card)',
    footerPaddingBlock: 16,
  },
  Menu: {
    itemBg: 'transparent',
    itemSelectedBg: primaryTint,
    itemSelectedColor: primaryColor,
    itemHoverBg: 'var(--menu-item-hover)',
    itemHoverColor: 'var(--foreground)',
    subMenuItemBg: 'transparent',
  },
  Dropdown: {
    colorBgElevated: 'var(--popover)',
    controlItemBgHover: 'var(--menu-item-hover)',
    controlItemBgActive: primaryTint,
  },
  Popover: {
    colorBgElevated: 'var(--popover)',
    colorText: 'var(--foreground)',
  },
  Tooltip: {
    colorBgSpotlight: 'var(--popover)',
    colorTextLightSolid: 'var(--foreground)',
  },
  Pagination: {
    itemActiveBg: primaryTint,
    itemBg: 'var(--card)',
    colorBgContainer: 'var(--card)',
  },
  Card: {
    borderRadiusLG: 12,
    colorBgContainer: 'var(--card)',
    colorBorderSecondary: 'var(--border)',
  },
  Tabs: {
    inkBarColor: primaryColor,
    itemSelectedColor: primaryColor,
    itemHoverColor: primaryColor,
    itemColor: 'var(--muted-foreground)',
  },
  Collapse: {
    headerBg: 'var(--card)',
    contentBg: 'var(--card)',
    colorBorder: 'var(--border)',
  },
  Switch: {
    colorPrimary: primaryColor,
    colorPrimaryHover: primaryHover,
  },
  Radio: {
    colorPrimary: primaryColor,
    buttonSolidCheckedBg: primaryColor,
    buttonSolidCheckedHoverBg: primaryHover,
  },
  Checkbox: {
    colorPrimary: primaryColor,
    colorPrimaryHover: primaryHover,
  },
  Slider: {
    trackBg: primaryColor,
    trackHoverBg: primaryHover,
    railBg: 'var(--muted)',
    handleColor: primaryColor,
  },
  Upload: {
    colorFillAlter: 'var(--muted)',
  },
  Spin: {
    colorPrimary: primaryColor,
  },
  Empty: {
    colorTextDisabled: 'var(--muted-foreground)',
  },
  Badge: {
    colorError: 'var(--destructive)',
  },
  Divider: {
    colorSplit: 'var(--border)',
  },
  Segmented: {
    trackBg: 'var(--muted)',
    itemSelectedBg: 'var(--card)',
    itemSelectedColor: primaryColor,
    itemHoverBg: 'var(--menu-item-hover)',
  },
  Alert: {
    colorInfoBg: 'var(--info-banner-bg)',
    colorInfoBorder: 'var(--info-banner-border)',
    colorWarningBg: 'var(--warning-subtle)',
    colorWarningBorder: 'var(--warning-border)',
    colorErrorBg: 'var(--danger-subtle)',
    colorSuccessBg: 'var(--status-online-bg)',
  },
};

export function getAntdTheme(isDark: boolean): ThemeConfig {
  return {
    algorithm: isDark ? antdThemeAlgorithm.darkAlgorithm : antdThemeAlgorithm.defaultAlgorithm,
    token: {
      colorPrimary: primaryColor,
      colorPrimaryHover: primaryHover,
      colorPrimaryActive: primaryHover,
      colorLink: primaryColor,
      colorLinkHover: primaryHover,
      borderRadius: parseInt(borderRadius, 10),
      fontFamily,
      controlHeight: 40,
      controlOutline: 'var(--ring)',
      colorBgContainer: 'var(--card)',
      colorBgElevated: 'var(--popover)',
      colorBgLayout: 'var(--background)',
      colorText: 'var(--foreground)',
      colorTextSecondary: 'var(--muted-foreground)',
      colorTextTertiary: 'var(--muted-foreground)',
      colorTextPlaceholder: 'var(--muted-foreground)',
      colorTextDisabled: 'var(--muted-foreground)',
      colorBorder: 'var(--border)',
      colorBorderSecondary: 'var(--border)',
      colorFillAlter: 'var(--muted)',
      colorFillSecondary: 'var(--muted)',
      colorFillTertiary: 'var(--surface-subtle)',
      colorError: 'var(--destructive)',
      colorBgTextHover: 'var(--menu-item-hover)',
      colorBgTextActive: primaryTint,
      colorBgMask: isDark
        ? 'oklch(0.141 0.005 285.823 / 0.55)'
        : 'rgba(15, 23, 42, 0.5)',
      boxShadow: 'var(--card-shadow)',
      boxShadowSecondary: 'var(--card-shadow-lg)',
    },
    components: sharedComponents,
  };
}

/** @deprecated use getAntdTheme(isDark) */
export const antdTheme = getAntdTheme(false);
